import { z } from "zod";

// =========================================================================
// SHARED
// =========================================================================

const AffiliateCodeSchema = z
  .string()
  .min(2)
  .max(50)
  .trim()
  .toLowerCase()
  .regex(
    /^[a-z0-9_-]+$/,
    "Solo letras minúsculas, números, guiones y guión bajo"
  );

// =========================================================================
// ENTIDAD AFFILIATE (uso interno admin)
// =========================================================================

export const AffiliateSchema = z.object({
  id: z.string(),
  name: z.string().min(1).max(255),
  email: z.email().optional().nullable(),
  whatsapp: z.string().optional().nullable(),
  notes: z.string().optional().nullable(),
  code: AffiliateCodeSchema,
  commission_pct: z.number().min(0).max(100).optional().nullable(),
  bonus_orders_per_period: z.number().int().positive().default(120),
  bonus_period_count: z.number().int().positive().default(2),
  max_uses: z.number().int().positive().optional().nullable(),
  expires_at: z.iso.datetime().optional().nullable(),
  is_active: z.boolean().default(true),
  created_at: z.iso.datetime(),
  updated_at: z.iso.datetime(),
  // Computed para el listado interno
  total_referrals: z.number().int().optional(),
  total_bonus_orders_used: z.number().int().optional(),
});

// =========================================================================
// POST /internal/affiliates
// =========================================================================

export const CreateAffiliateRequestSchema = AffiliateSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
  total_referrals: true,
  total_bonus_orders_used: true,
});

export const CreateAffiliateResponseSchema = AffiliateSchema;

// =========================================================================
// PATCH /internal/affiliates/:id
// =========================================================================

export const UpdateAffiliateRequestSchema =
  CreateAffiliateRequestSchema.partial();

// =========================================================================
// GET /internal/affiliates  (dashboard interno con stats agregados)
// =========================================================================

export const AffiliateListItemSchema = AffiliateSchema.required({
  total_referrals: true,
  total_bonus_orders_used: true,
});

// =========================================================================
// POST /admin/affiliate/redeem  (merchant registra su código, welcome page)
// =========================================================================

export const RedeemAffiliateCodeRequestSchema = z.object({
  code: AffiliateCodeSchema,
});

export const RedeemAffiliateCodeResponseSchema = z.object({
  affiliate_name: z.string(),
  bonus_orders_per_period: z.number().int(),
  bonus_period_count: z.number().int(),
  // Cuántos bonus quedan en el mes actual
  current_period_remaining: z.number().int(),
});

// Errores específicos del redeem como discriminated union
// para que el frontend pueda manejarlos granularmente sin parsear strings
export const RedeemAffiliateCodeErrorSchema = z.discriminatedUnion("code", [
  z.object({ code: z.literal("ALREADY_REDEEMED") }),
  z.object({ code: z.literal("CODE_NOT_FOUND") }),
  z.object({ code: z.literal("CODE_INACTIVE") }),
  z.object({ code: z.literal("CODE_EXPIRED") }),
  z.object({ code: z.literal("CODE_MAX_USES_REACHED") }),
]);

// =========================================================================
// GET /admin/affiliate/status  (merchant consulta su estado, welcome page)
// =========================================================================

export const AffiliateStatusResponseSchema = z.discriminatedUnion(
  "has_affiliate",
  [
    z.object({
      has_affiliate: z.literal(false),
    }),
    z.object({
      has_affiliate: z.literal(true),
      affiliate_name: z.string(),
      code: z.string(),
      bonus_orders_per_period: z.number().int(),
      bonus_period_count: z.number().int(),
      periods_completed: z.number().int(),
      // Lazy-computed al momento del request
      current_period_remaining: z.number().int(),
      total_bonus_used: z.number().int(),
      registered_at: z.iso.datetime(),
      // null si el beneficio sigue activo
      benefit_expires_after: z.iso.datetime().nullable(),
    }),
  ]
);

// =========================================================================
// TYPE EXPORTS
// =========================================================================

export type Affiliate = z.infer<typeof AffiliateSchema>;
export type AffiliateListItem = z.infer<typeof AffiliateListItemSchema>;
export type CreateAffiliateRequest = z.infer<
  typeof CreateAffiliateRequestSchema
>;
export type UpdateAffiliateRequest = z.infer<
  typeof UpdateAffiliateRequestSchema
>;
export type RedeemAffiliateCodeRequest = z.infer<
  typeof RedeemAffiliateCodeRequestSchema
>;
export type RedeemAffiliateCodeResponse = z.infer<
  typeof RedeemAffiliateCodeResponseSchema
>;
export type RedeemAffiliateCodeError = z.infer<
  typeof RedeemAffiliateCodeErrorSchema
>;
export type AffiliateStatusResponse = z.infer<
  typeof AffiliateStatusResponseSchema
>;
